import { ReebeloConditions } from '@lambda/reebelo/src/types';
import {
  CartLineReferenceType,
  CartLineT,
  LineItemType,
  NoteT,
} from '@lambda/apis/src/cart/types';
import { WarrantyT } from '@lambda/apis/src/warranty/types';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import type {
  DeliveryEstimateT,
  OfferT,
} from '@lambda/apis/src/catalog-v2/types';
import 'dayjs/locale/de';
import 'dayjs/locale/it';
import settings from '@/settings';
import i18n from '@/settings/i18n';
import { formatStoreDateRange } from '@/lib/intl-format';
import {
  formatWarrantyInfo,
  getWarrantyInfoByCategoryPks,
  normalizeWarrantyLength,
} from '@/lib/warranty';
import { NoteNames } from '../product/hooks/notes';
import {
  encodeDecodeForNote,
  generateAddCartDateTimeNote,
  generateIdNote,
} from '@/utils/hooks/cart/helpers';
import { AddToCartParams } from '@/utils/hooks/cart/types';

// Builds the notes for an express-shipping add-on line from its parent product
// line — mirrors `generateAddOnsNotes` but sourced from the cart line, since
// the cart has no product/sku/offer objects.
export const buildExpressShippingNotes = (parent: CartLineT): NoteT[] => {
  const idNoteNames: string[] = [
    NoteNames.ID,
    NoteNames.SKU_ID,
    NoteNames.PRODUCT_ID,
  ];

  const carriedOverNotes = (parent.notes ?? []).filter((note) =>
    idNoteNames.includes(note.name),
  );

  const addedAt =
    parent.notes?.find((note) => note.name === NoteNames.ADD_TO_CART_DATE_TIME)
      ?.value ?? new Date().toISOString();

  const notes: NoteT[] = [
    ...carriedOverNotes,
    { name: NoteNames.ADD_TO_CART_DATE_TIME, value: addedAt },
    { name: NoteNames.FOR, value: `[${parent.sku}] ${parent.title}` },
  ];

  if (parent.category?.name) {
    notes.push({ name: NoteNames.OFFER_CATEGORY, value: parent.category.name });
  }

  return notes;
};

const ACCESSORY_LINE_TYPES = ['ACCESSORY', 'CART_RECOMMENDATION'];

export const getRecommendationParentId = (
  line: CartLineT,
): string | undefined =>
  line.notes?.find((note) => note.name === NoteNames.RECOMMENDED_FOR)?.value;

export const isOrphanedRecommendation = (
  line: CartLineT,
  cartLines: CartLineT[],
): boolean => {
  const type = line.notes?.find((note) => note.name === NoteNames.TYPE)?.value;

  if (type !== 'CART_RECOMMENDATION') {
    return false;
  }

  const parentId = getRecommendationParentId(line);

  if (!parentId) {
    return true;
  }

  return !cartLines.some((candidate) => candidate.id === parentId);
};

// Everything that leaves the cart together with `lines`: the recommendations
// added under any of them, then the lines themselves. MsCart drops each line's
// own warranty and express-shipping add-ons, so those are not listed here.
export const collectLinesToRemove = (
  lines: CartLineT[],
  cartLines: CartLineT[],
): CartLineT[] => {
  const parentIds = new Set(lines.map((line) => line.id));

  const attached = cartLines.filter((cartLine) => {
    const parentId = getRecommendationParentId(cartLine);

    return parentId !== undefined && parentIds.has(parentId);
  });

  const seen = new Set<string>();

  return [...attached, ...lines].filter((line) => {
    if (seen.has(line.id)) {
      return false;
    }

    seen.add(line.id);

    return true;
  });
};

export const groupLinesBySku = (lines: CartLineT[]): CartLineT[][] => {
  const groups = new Map<string, CartLineT[]>();

  lines.forEach((line) => {
    const skuId = line.notes?.find(
      (note) => note.name === NoteNames.SKU_ID,
    )?.value;
    const key = skuId || `line:${line.id}`;
    const group = groups.get(key);

    if (group) {
      group.push(line);
    } else {
      groups.set(key, [line]);
    }
  });

  return Array.from(groups.values());
};

/**
 * Cards in the order they were first added to the cart, oldest first.
 * MsCart puts each new line at the top of the cart, so a card's first-added
 * line is the one furthest down `cartLines`: cards are ordered by that. A SKU
 * later bought from another seller keeps its card's place.
 */
export const orderByFirstAdded = (
  groups: CartLineT[][],
  cartLines: CartLineT[],
): CartLineT[][] => {
  const position = new Map(cartLines.map((line, index) => [line.id, index]));
  const firstAdded = (group: CartLineT[]) =>
    Math.max(...group.map((line) => position.get(line.id) ?? -1));

  return [...groups].sort((a, b) => firstAdded(b) - firstAdded(a));
};

// Notes that describe the offer a line was bought from rather than the product,
// so they have to be rebuilt when the same SKU is added from another vendor.
const OFFER_SPECIFIC_NOTES: string[] = [
  NoteNames.ID,
  NoteNames.ADD_TO_CART_DATE_TIME,
  NoteNames.CUTOFF_TIME,
  NoteNames.CUTOFF_TIMEZONE,
  NoteNames.HANDLING_TIME,
  NoteNames.DISPATCH_MIN,
  NoteNames.DISPATCH_MAX,
];

export const buildSiblingOfferNotes = (
  parent: CartLineT,
  offer: OfferT,
): NoteT[] => {
  const standard = offer.deliveryEstimates?.find(
    (estimate) => estimate.type === 'standard',
  );

  const deliveryNotes: NoteT[] = standard
    ? [
        { name: NoteNames.CUTOFF_TIME, value: standard.cutoffTime ?? '' },
        {
          name: NoteNames.CUTOFF_TIMEZONE,
          value: standard.cutoffTimeZone ?? '',
        },
        {
          name: NoteNames.HANDLING_TIME,
          value: standard.handlingTime?.toString() ?? '',
        },
      ]
    : [];

  return [
    ...(parent.notes ?? []).filter(
      (note) => !OFFER_SPECIFIC_NOTES.includes(note.name),
    ),
    ...generateIdNote(offer.vendor?.name ?? '', offer.vendorSku ?? ''),
    ...generateAddCartDateTimeNote(),
    ...deliveryNotes,
  ];
};

/**
 * The delivery window for a SKU coming from several sellers: the earliest any
 * of them could arrive through to the latest. Shoppers get one range rather
 * than a date per vendor, and it is honest about the last parcel.
 *
 * Returns null when no seller has a standard estimate, so callers can fall back
 * to the dates on the line notes.
 */
export const combineDeliveryRange = (
  estimates: (DeliveryEstimateT[] | undefined)[],
): { minDeliveryDate: string; maxDeliveryDate: string } | null => {
  const standard = estimates
    .map((list) => list?.find((estimate) => estimate.type === 'standard'))
    .filter((estimate): estimate is DeliveryEstimateT => Boolean(estimate));

  if (standard.length === 0) {
    return null;
  }

  // ISO dates sort lexicographically, so no parsing is needed.
  const minDates = standard.map((estimate) => estimate.minDate).sort();
  const maxDates = standard.map((estimate) => estimate.maxDate).sort();

  return {
    minDeliveryDate: minDates[0],
    maxDeliveryDate: maxDates[maxDates.length - 1],
  };
};

/**
 * What a unit of a SKU averages out at when it is coming from several sellers
 * at different prices — the total paid divided by the units bought, in minor
 * units. Weighted by quantity, so a large cheap allocation pulls it down more
 * than a single expensive one.
 */
export const averageUnitPrice = (lines: CartLineT[]): number => {
  const quantity = lines.reduce((total, line) => total + line.quantity, 0);

  if (quantity === 0) {
    return lines[0]?.price ?? 0;
  }

  const paid = lines.reduce(
    (total, line) => total + line.price * line.quantity,
    0,
  );

  return Math.round(paid / quantity);
};

export const isAccessoryLine = (line: CartLineT): boolean => {
  const type = line.notes?.find((note) => note.name === NoteNames.TYPE)?.value;

  return type != null && ACCESSORY_LINE_TYPES.includes(type);
};

export const buildReebeloCareLine = (
  device: CartLineT,
  warranty: WarrantyT,
): AddToCartParams => {
  const sku = device.sku ?? '';

  return {
    typeId: warranty.id,
    type: LineItemType.WARRANTY,
    notes: [
      ...encodeDecodeForNote.encode({ sku, title: device.title ?? '' }),
      ...generateIdNote(device.vendor ?? '', sku),
      ...generateAddCartDateTimeNote(),
      { name: '_offer_category', value: device.category?.name ?? '' },
    ],
    quantity: device.quantity,
    reference: {
      typeId: device.id,
      type: CartLineReferenceType.CART_LINE,
    },
  };
};

export const getCartLineSkuId = (line: CartLineT): string | null =>
  line.notes?.find((note) => note.name === '_sku_id')?.value ?? null;

export const getCartLineProductLink = (line: CartLineT): string | null => {
  const notes = line.notes ?? [];
  const productId = notes.find((note) => note.name === '_product_id')?.value;
  const skuId = notes.find((note) => note.name === '_sku_id')?.value;

  if (!productId) {
    return null;
  }

  return `/collections/${encodeURIComponent(productId)}${
    skuId ? `?skuId=${encodeURIComponent(skuId)}` : ''
  }`;
};

// Products already in the cart are not worth recommending, so their pskus are
// excluded. Only product lines count: MsCart gives shipping and warranty lines
// their own SKU id as a psku, and letting those in would change the list (and
// refetch every line's recommendations) whenever delivery or cover is toggled.
// Accessories are deliberately left in: a recommended accessory's card becomes
// a quantity stepper once it is in the cart, so excluding it would make the
// card vanish the moment it was added.
export const getRecommendationExclusions = (cartLines: CartLineT[]): string[] =>
  Array.from(
    new Set(
      cartLines
        .filter(
          (line) =>
            line.type === LineItemType.PRODUCT && !isAccessoryLine(line),
        )
        .map((line) => line.psku)
        .filter((psku): psku is string => Boolean(psku)),
    ),
  );

export const formatDeliveryDates = ({
  minDeliveryDate,
  maxDeliveryDate,
}: {
  minDeliveryDate: string;
  maxDeliveryDate: string;
}) => {
  if (!minDeliveryDate) {
    return '';
  }

  // EU stores: Intl in the store's locale ("6.–7. Oktober", "6–7 octobre").
  const storeRange = formatStoreDateRange(
    dayjs(minDeliveryDate),
    dayjs(maxDeliveryDate || minDeliveryDate),
    'dayMonth',
  );

  if (storeRange) {
    return storeRange;
  }

  const locale = i18n.getLocale();
  const isFr = locale === 'fr';
  // Italian takes the French shape ("28 settembre", "28-29 settembre"), with
  // "il" in place of "le" for a single day.
  const isIt = locale === 'it';
  // German drops the "by" prefix entirely ("Kostenlose Lieferung 2.-3. Sep",
  // not "... by Sep 2-3") — the "Free Delivery" label it's appended to
  // already implies an estimate, so a leading "bis"/"by" reads redundant.
  const isDe = locale === 'de';
  const byPrefix = isDe ? '' : 'by ';
  const minDate = dayjs(minDeliveryDate).locale(locale);
  const maxDate = dayjs(maxDeliveryDate).locale(locale);

  // French, Italian and German all write the day before the month ("6 juillet"
  // / "6 luglio" / "6. Juli", German with a trailing period on the day). Ranges use a plain
  // dash rather than "du … au …", which reads as an offer running between
  // those dates instead of a delivery estimate.
  if (minDate.isSame(maxDate, 'day')) {
    if (isFr || isIt) {
      return `${isFr ? 'le' : 'il'} ${minDate.format('D MMMM')}`;
    }

    if (isDe) {
      return minDate.format('D. MMMM');
    }

    return `${byPrefix}${minDate.format('MMMM D')}`;
  }

  if (minDate.isSame(maxDate, 'month')) {
    if (isFr || isIt) {
      return `${minDate.format('D')}-${maxDate.format('D MMMM')}`;
    }

    if (isDe) {
      return `${minDate.format('D')}.-${maxDate.format('D. MMMM')}`;
    }

    return `${byPrefix}${minDate.format('MMMM D')}-${maxDate.format('D')}`;
  }

  if (isFr || isIt) {
    return `${minDate.format('D MMMM')} - ${maxDate.format('D MMMM')}`;
  }

  if (isDe) {
    return `${minDate.format('D. MMMM')} - ${maxDate.format('D. MMMM')}`;
  }

  return `${byPrefix}${minDate.format('MMMM D')} - ${maxDate.format('MMMM D')}`;
};

export const generateCartWarrantyLabel = async ({
  conditions,
  categoryPks,
  freeReturnDays,
}: {
  conditions: string[];
  categoryPks: string[];
  freeReturnDays: number;
}) => {
  const t = i18n.init('cart');

  const checkItemCondition = (
    streamlinedCondition: string,
    method: 'some' | 'every' = 'some',
  ) => conditions[method]((condition) => condition === streamlinedCondition);

  const checkCategoryPks = async () => {
    const warrantyInfoArray = await getWarrantyInfoByCategoryPks(categoryPks);

    if (!warrantyInfoArray?.length) {
      return {
        warrantyLength: settings.warranty_month,
        warrantyUnit: 'Month',
        eligible: true,
      };
    }
    const ineligibleWarranties = warrantyInfoArray.filter(
      (warrantyInfo) => !warrantyInfo?.eligible,
    );

    const eligibleWarranties = warrantyInfoArray.filter(
      (warrantyInfo) => warrantyInfo?.eligible,
    );

    if (ineligibleWarranties.length > 0 && eligibleWarranties.length > 0) {
      return {
        warrantyLength: settings.warranty_month,
        warrantyUnit: 'Month',
        eligible: false,
        mixedCart: true,
      };
    }

    if (eligibleWarranties.length === 0) {
      return {
        warrantyLength: settings.warranty_month,
        warrantyUnit: 'month',
        eligible: true,
      };
    }

    const firstWarranty = eligibleWarranties[0];
    const allSame = eligibleWarranties.every(
      (warranty) =>
        warranty.warrantyLength === firstWarranty.warrantyLength &&
        warranty.warrantyUnit === firstWarranty.warrantyUnit,
    );

    if (allSame) {
      // If all warranties are the same, return the warranty length and unit
      return {
        warrantyLength: firstWarranty.warrantyLength,
        warrantyUnit: firstWarranty.warrantyUnit,
        eligible: true,
      };
    }

    // If they differ, find the longest warranty in terms of days
    const longestWarranty = eligibleWarranties.reduce((longest, current) => {
      const longestInDays = normalizeWarrantyLength(
        longest?.warrantyLength,
        longest.warrantyUnit,
      );
      const currentInDays = normalizeWarrantyLength(
        current?.warrantyLength,
        current.warrantyUnit,
      );

      return currentInDays > longestInDays ? current : longest;
    });

    return {
      warrantyLength: longestWarranty.warrantyLength,
      warrantyUnit: longestWarranty.warrantyUnit,
      eligible: true,
      mixedCart: eligibleWarranties.length > 1,
    };
  };

  const allItemsAreBrandNew = checkItemCondition(
    ReebeloConditions.BrandNew,
    'every',
  );

  const returnDayLabel = t`${{ days: freeReturnDays }}-Day Free Returns`;

  if (allItemsAreBrandNew) {
    return `${returnDayLabel} • ${t`1 Year Warranty`}`;
  }

  if (['quista'].includes(settings.store)) {
    return `${returnDayLabel} • ${t`3 Months Warranty`}`;
  }

  const warrantyInfo = await checkCategoryPks();

  const warrantyLengthInDays = normalizeWarrantyLength(
    warrantyInfo.warrantyLength,
    warrantyInfo.warrantyUnit,
  );

  if (warrantyLengthInDays <= freeReturnDays) {
    return `${returnDayLabel}`;
  }

  const formattedWarrantyInfo = formatWarrantyInfo(
    warrantyInfo,
    warrantyInfo.mixedCart,
  );

  if (formattedWarrantyInfo) {
    return `${returnDayLabel} • ${t`${{
      warranty: formattedWarrantyInfo,
    }} Warranty`}`;
  }

  return `${returnDayLabel} • ${t`${{
    months: settings.warranty_month,
  }} Months Warranty`}`;
};
